import { useEffect } from 'react';
import './SteamEruptionAnimation.css';

/**
 * PROFESSIONAL STEAM ERUPTION ANIMATION
 * Water-type special move - superheated steam blast
 * 
 * Key Features:
 * - Water-type special attack (110 base power, 95% accuracy)
 * - 30% chance to burn
 * - Volcanion signature move with scalding steam geyser
 * - Professional gaming quality with steam and heat effects
 */
export default function SteamEruptionAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Steam Pressure Buildup (0-600ms)
    playerEl.classList.add('steam-eruption-user-charge');

    const pressureAura = document.createElement('div');
    pressureAura.className = 'steam-eruption-pressure-aura';
    pressureAura.style.left = `${playerX - 80}px`;
    pressureAura.style.top = `${playerY - 80}px`;
    container.appendChild(pressureAura);
    elements.push(pressureAura);

    // Rising steam wisps around player
    for (let i = 0; i < 12; i++) {
      const wisp = document.createElement('div');
      wisp.className = 'steam-eruption-wisp';
      wisp.style.left = `${playerX + (Math.random() - 0.5) * 80 - 8}px`;
      wisp.style.top = `${playerY + (Math.random() - 0.5) * 40 - 8}px`;
      wisp.style.animationDelay = `${i * 40}ms`;
      container.appendChild(wisp);
      elements.push(wisp);
    }

    // Heat shimmer particles
    for (let i = 0; i < 8; i++) {
      const shimmer = document.createElement('div');
      shimmer.className = 'steam-eruption-heat-shimmer';
      const angle = (i / 8) * 360;
      const radius = 55;
      shimmer.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 6}px`;
      shimmer.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 6}px`;
      shimmer.style.animationDelay = `${i * 60}ms`;
      container.appendChild(shimmer);
      elements.push(shimmer);
    }

    // PHASE 2: Steam Blast (600-1200ms)
    const phase2Timer = setTimeout(() => {
      // Steam jet projectile
      const steamJet = document.createElement('div');
      steamJet.className = 'steam-eruption-jet';
      steamJet.style.left = `${playerX}px`;
      steamJet.style.top = `${playerY}px`;
      steamJet.style.setProperty('--dx', `${dx}px`);
      steamJet.style.setProperty('--dy', `${dy}px`);
      container.appendChild(steamJet);
      elements.push(steamJet);

      // Steam cloud trail along path
      for (let i = 0; i < 18; i++) {
        const cloudTimer = setTimeout(() => {
          const cloud = document.createElement('div');
          cloud.className = 'steam-eruption-cloud';
          const progress = i / 18;
          cloud.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 30 - 15}px`;
          cloud.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 30 - 15}px`;
          container.appendChild(cloud);
          elements.push(cloud);
        }, i * 28);
        elements.push({ timer: cloudTimer, type: 'timer' });
      }

      // Hot water droplets
      for (let i = 0; i < 14; i++) {
        const dropTimer = setTimeout(() => {
          const drop = document.createElement('div');
          drop.className = 'steam-eruption-hot-drop';
          const progress = i / 14;
          drop.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 25 - 4}px`;
          drop.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 25 - 4}px`;
          container.appendChild(drop);
          elements.push(drop);
        }, i * 35);
        elements.push({ timer: dropTimer, type: 'timer' });
      }
    }, 600);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Scalding Impact (1200-1800ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('steam-eruption-opponent-hit');

      // Massive steam explosion
      const explosion = document.createElement('div');
      explosion.className = 'steam-eruption-explosion';
      explosion.style.left = `${opponentX - 120}px`;
      explosion.style.top = `${opponentY - 120}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Steam geyser columns shooting upward
      for (let i = 0; i < 5; i++) {
        const geyserTimer = setTimeout(() => {
          const geyser = document.createElement('div');
          geyser.className = 'steam-eruption-geyser';
          geyser.style.left = `${opponentX + (i - 2) * 25 - 15}px`;
          geyser.style.top = `${opponentY - 60}px`;
          container.appendChild(geyser);
          elements.push(geyser);
        }, i * 60);
        elements.push({ timer: geyserTimer, type: 'timer' });
      }

      // Scalding steam burst
      for (let i = 0; i < 20; i++) {
        const burstAngle = (i / 20) * 360;
        const burstDist = 60 + Math.random() * 50;
        const burst = document.createElement('div');
        burst.className = 'steam-eruption-burst';
        burst.style.left = `${opponentX - 6}px`;
        burst.style.top = `${opponentY - 6}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 15}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }

      // Steam shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'steam-eruption-shockwave';
          sw.style.left = `${opponentX - 70}px`;
          sw.style.top = `${opponentY - 70}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }
    }, 1200);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Burn Aftermath (1800-2200ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('steam-eruption-user-charge');

      // Lingering steam clouds
      for (let i = 0; i < 8; i++) {
        const linger = document.createElement('div');
        linger.className = 'steam-eruption-linger';
        linger.style.left = `${opponentX + (Math.random() - 0.5) * 120 - 12}px`;
        linger.style.top = `${opponentY + (Math.random() - 0.5) * 80 - 12}px`;
        linger.style.animationDelay = `${i * 40}ms`;
        container.appendChild(linger);
        elements.push(linger);
      }
    }, 1800);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (2200ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('steam-eruption-user-charge');
      opponentEl.classList.remove('steam-eruption-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 2200);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('steam-eruption-user-charge');
      opponentEl.classList.remove('steam-eruption-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

SteamEruptionAnimation.moveType = 'water';
